// Split url into two parts , persona and screen
// E.g. analytics-gates-live => persona: analytics , screen: gates-live
import { routes } from "../../routes/routes"
import { useFeaturesStore } from "../../store/featuresStore"
import { Persona } from "../../types/Custom/Types"
import Auth from "../../utils/auth"

// Check if pathname is in multiple branches mode (starts with "branches-")
export const isMultipleBranchesMode = (pathname: string): boolean => {
  return pathname.split("/").some((segment) => segment.startsWith("branches-"))
}

// used to extract current active persona and screen
export const splitPersonaAndScreen = (url: string): { persona: Persona; screen: string } => {
  const [persona, ...screenUrl] = url.split("-")

  const screen = screenUrl.join("-")

  return { persona: persona as Persona, screen }
}

// Get the active screen from the given route
export const getActiveScreen = (route: string): string => {
  // get the actual screen route not the whole parent/child route
  // E.g => heatmap-floor/analytics-heatmap-live
  const pagePathName = route?.split("/").reverse()
  // E.g => ["analytics-heatmap-live","heatmap-floor"] reversed and splitted by "/"
  const routeWithoutPersona = splitPersonaAndScreen(pagePathName[0])
  // split analytics-heatmap-live into persona : analytics , screen: heatmap-live
  return routeWithoutPersona.screen
}

type Mode = "single" | "multiple"

interface GetFirstScreenOptions {
  persona: Persona
  mode?: Mode
}

// get first authorized and active screen for the given persona and mode
// called when toggling personas and when first deciding landing screen.
export const getFirstAuthorizedAndActiveScreen = ({ persona, mode = "single" }: GetFirstScreenOptions): string => {
  const features = useFeaturesStore.getState().features

  // Handle multiple branches mode
  if (mode === "multiple" && Auth.hasFeaturePermission("multiple_branches")) {
    if (Auth.hasFeaturePermission("people_count") && features?.analytics?.counter) {
      return `/${routes?.branchesGatesAnalytics}`
    } else if (Auth.hasFeaturePermission("cameras")) {
      return `/${routes?.branchesCamerasConnectivity}`
    } else if (Auth.hasFeaturePermission("nodes")) {
      return `/${routes?.branchesNodesConnectivity}`
    }
    return `/${routes?.noPermissions}`
  }

  // Handle single mode (existing logic)
  if (persona === "analytics") {
    if (Auth.hasFeaturePermission("people_count") && features?.analytics?.counter) {
      return `/${routes?.gates}/${routes?.liveGates}`
    } else if (Auth.hasFeaturePermission("missing_data") && features?.analytics?.counter) {
      return `/${routes?.missingData}`
    } else if (Auth.hasFeaturePermission("zones_occupancy") && features?.analytics?.occupancy) {
      return `/${routes?.staffOccupancy}`
    } else if (Auth.hasFeaturePermission("heatmap") && features?.analytics?.heatmap) {
      return `/${routes?.heatmap}`
    } else if (Auth.hasFeaturePermission("mcp") && features?.analytics?.mcp) {
      return `/${routes?.floorsMCP}`
    } else if (Auth.hasFeaturePermission("cars_analytics") && features?.analytics?.cars) {
      return `/${routes?.carsAnalytics}`
    }
  } else if (persona === "admin") {
    if (Auth.hasFeaturePermission("cameras")) {
      return `/${routes?.cameraList}`
    } else if (Auth.hasFeaturePermission("floor")) {
      return `/${routes?.floors}`
    } else if (Auth.hasFeaturePermission("categories")) {
      return `/${routes?.categoriesList}`
    } else if (Auth.hasFeaturePermission("entities")) {
      return `/${routes?.entitiesAndRegions}`
    } else if (Auth.hasFeaturePermission("intrusion_setup")) {
      return `/${routes?.intrusionSetup}`
    } else if (Auth.hasFeaturePermission("nodes")) {
      return `/${routes?.nodesList}`
    } else if (Auth.hasFeaturePermission("users")) {
      return `/${routes?.users}`
    } else if (Auth.hasFeaturePermission("api_integration")) {
      return `/${routes?.apiIntegration}`
    } else if (Auth.hasFeaturePermission("cars_admin")) {
      return `/${routes?.carsList}`
    } else if (Auth.hasFeaturePermission("defect_detection_setup")) {
      return `/${routes?.defectDetectionSetup}`
    }
  } else if (persona === "marketing") {
    if (Auth.hasFeaturePermission("campaign") && features?.marketing?.campaign) {
      return `/${routes?.campaign}`
    }
  } else if (persona === "security") {
    if (Auth.hasFeaturePermission("security_camera_stream")) {
      return `/${routes?.liveStreams}`
    } else if (Auth.hasFeaturePermission("security_events")) {
      return `/${routes?.securityIncidents}`
    } else if (Auth.hasFeaturePermission("cars_security") && features?.security?.cars) {
      return `/${routes?.carsLogs}`
    } else if (Auth.hasFeaturePermission("facial_recognition") && features?.security?.face) {
      return `/${routes?.faceRecognition}`
    }
  } else if (persona === "industrial") {
    if (Auth.hasFeaturePermission("defect_detection") && features?.industrial?.defect_detection) {
      return `/${routes?.defectDetection}`
    }
  }
  return `/${routes?.noPermissions}`
}
